import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Maximize2, X, Phone, Mail } from 'lucide-react';
import { siteMeta, contactInfo, serviceSeoMeta } from '../data';
import { serviceStructuredContent } from '../data/serviceContent';
import { imageUrl } from '../lib/images';

function StructuredContent({ items, benefitsHtml }) {
  let benefitsRendered = false;

  const renderBenefitsBox = () => {
    if (!benefitsHtml || benefitsRendered) return null;
    benefitsRendered = true;
    return (
      <div key="benefits-box" className="my-10 bg-stone-50 border-l-4 border-[#285c35] p-7 md:p-9 shadow-sm rounded-r-lg">
        <h3 className="text-2xl font-bold mb-4 text-stone-900 flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-[#285c35] shrink-0" />
          Fordele ved denne ydelse
        </h3>
        <div 
          className="text-stone-700 text-base sm:text-lg [&_ul]:space-y-2 [&_li]:flex [&_li]:items-start [&_li]:gap-2.5 [&_li]:leading-relaxed"
          dangerouslySetInnerHTML={{ __html: benefitsHtml }}
        />
      </div>
    );
  };

  const renderedElements = [];

  items.forEach((item, index) => {
    // Insert the benefits box right before the heading about price ("Pris på...")
    if (
      item.type === 'heading' &&
      item.level === 2 &&
      typeof item.text === 'string' &&
      item.text.toLowerCase().startsWith('pris på') &&
      !benefitsRendered
    ) {
      const box = renderBenefitsBox();
      if (box) renderedElements.push(box);
    }

    if (item.type === 'heading') {
      if (item.level === 1) {
        renderedElements.push(
          <h1 key={`h1-${index}`} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-stone-900 tracking-tight mb-6 mt-1 leading-[1.2]">
            {item.text}
          </h1>
        );
      } else if (item.level === 2) {
        renderedElements.push(
          <h2 key={`h2-${index}`} className="text-2xl sm:text-3xl font-bold text-stone-900 tracking-tight mt-12 mb-4 pt-2">
            {item.text}
          </h2>
        );
      } else if (item.level === 3) {
        renderedElements.push(
          <h3 key={`h3-${index}`} className="text-xl sm:text-2xl font-bold text-stone-900 tracking-tight mt-8 mb-3">
            {item.text}
          </h3>
        );
      }
    } else if (item.type === 'paragraph') {
      renderedElements.push(
        <p 
          key={`p-${index}`}
          className="text-base sm:text-lg text-stone-700 leading-relaxed mb-5 [&_a]:text-[#285c35] [&_a]:underline [&_a]:font-semibold hover:[&_a]:text-[#1f4729] [&_strong]:text-stone-900 [&_strong]:font-bold"
          dangerouslySetInnerHTML={{ __html: item.html }}
        />
      );
    } else if (item.type === 'list') {
      renderedElements.push(
        <ul key={`ul-${index}`} className="space-y-3 my-6 pl-1 sm:pl-2">
          {item.items.map((listItem, i) => (
            <li key={i} className="flex items-start gap-3 text-base sm:text-lg text-stone-700 leading-relaxed">
              <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#285c35] mt-2.5 shrink-0" />
              <span>{listItem}</span>
            </li>
          ))}
        </ul>
      );
    } else if (item.type === 'cta') {
      renderedElements.push(
        <div key={`cta-${index}`} className="my-8 flex flex-wrap gap-4 items-center not-prose">
          {item.buttons.map((btn, bIdx) => {
            const isTel = btn.url.startsWith('tel:');
            const isMail = btn.url.startsWith('mailto:');
            const isPrimary = btn.style === 'primary';
            return (
              <a
                key={bIdx}
                href={btn.url}
                className={
                  isPrimary
                    ? "inline-flex items-center gap-2.5 bg-[#285c35] text-white px-6 py-3.5 rounded-md font-bold hover:bg-[#1f4729] transition-all shadow-sm hover:shadow text-base no-underline"
                    : "inline-flex items-center gap-2.5 bg-white text-[#285c35] border-2 border-[#285c35] px-6 py-3 rounded-md font-bold hover:bg-stone-50 transition-all shadow-sm hover:shadow text-base no-underline"
                }
              >
                {isTel && <Phone className="w-4 h-4" />}
                {isMail && <Mail className="w-4 h-4" />}
                <span>{btn.label}</span>
              </a>
            );
          })}
        </div>
      );
    }
  });

  // Fallback: If benefits wasn't inserted yet, append it now
  if (!benefitsRendered && benefitsHtml) {
    const box = renderBenefitsBox();
    if (box) renderedElements.push(box);
  }

  return <div className="space-y-1">{renderedElements}</div>;
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [heroImageIndex, setHeroImageIndex] = useState(0);

  useEffect(() => {
    setLoading(true);
    
    const fetchService = fetch(`/_api/public/by-slug/da/${slug}`).then(res => {
      if (!res.ok) throw new Error('Failed to fetch service');
      return res.json();
    });

    const fetchGallery = fetch('/_api/public/content/gallery').then(res => {
      if (!res.ok) throw new Error('Failed to fetch gallery');
      return res.json();
    });

    Promise.all([fetchService, fetchGallery])
      .then(([serviceData, galleryData]) => {
        setService(serviceData);
        const relatedGallery = galleryData.filter(item => {
          const itemCat = item.category?.da;
          const serviceTitle = serviceData.title?.da;
          if (serviceTitle === "Tynding/Sporlægning" && itemCat === "Tynding") {
            return true;
          }
          if (serviceTitle === "Skovrejsning/Læhegn" && itemCat === "Skovrejsning") {
            return true;
          }
          return itemCat === serviceTitle;
        });
        setGalleryItems(relatedGallery);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching data:', err);
        setLoading(false);
      });
  }, [slug]);

  const allImages = galleryItems.flatMap(item => 
    (item.image || []).map((imgUrl, index) => ({
      url: imgUrl,
      title: item.title?.da,
      id: `${item.id}-${index}`
    }))
  );

  const heroImages = allImages.length > 0 
    ? allImages.map(img => img.url) 
    : (service?.image || []);

  useEffect(() => {
    setHeroImageIndex(0);
  }, [heroImages.length]);

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const timer = setInterval(() => {
      setHeroImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroImages]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Indlæser...</div>;
  if (!service) return <div className="min-h-screen flex items-center justify-center">Ydelse ikke fundet.</div>;

  const metaTitle = service.seo_title?.da || serviceSeoMeta[slug]?.title || `${service.title?.da} | ${siteMeta.title}`;
  const metaDesc = service.seo_description?.da || serviceSeoMeta[slug]?.description || service.short_description?.da;

  const structuredData = serviceStructuredContent[slug];

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-stone-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <AnimatePresence mode="wait">
            {heroImages.length > 0 && (
              <motion.img
                key={heroImageIndex}
                src={imageUrl(heroImages[heroImageIndex], 'width=1920,quality=85,format=auto')}
                alt={`${service.title?.da || 'Skovservice'} på Mors og i Thy - TH Skovservice`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
                className="w-full h-full object-cover"
              />
            )}
          </AnimatePresence>
        </div>
        <div className="container mx-auto px-4 relative z-10 mt-12">
          <Link to="/ydelser" className="inline-flex items-center gap-2 text-emerald-400 mb-8 hover:text-emerald-300 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Tilbage til ydelser
          </Link>
          {structuredData ? (
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[2rem] md:text-7xl font-bold mb-6 tracking-tight"
            >
              {service.title?.da}
            </motion.p>
          ) : (
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[2rem] md:text-7xl font-bold mb-6 tracking-tight"
            >
              {service.title?.da}
            </motion.h1>
          )}
          <p className="text-base md:text-xl text-stone-300 max-w-2xl leading-relaxed">
            {service.short_description?.da}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            <div className="lg:col-span-2">
              {structuredData ? (
                <StructuredContent 
                  items={structuredData.content} 
                  benefitsHtml={service.benefits?.da} 
                />
              ) : (
                <div className="space-y-10">
                  <div 
                    className="prose max-w-none"
                    dangerouslySetInnerHTML={{ __html: service.long_description?.da || '' }}
                  />
                  {service.benefits?.da && (
                    <div className="my-10 bg-stone-50 border-l-4 border-[#285c35] p-7 md:p-9 shadow-sm rounded-r-lg">
                      <h3 className="text-2xl font-bold mb-4 text-stone-900 flex items-center gap-3">
                        <CheckCircle2 className="w-6 h-6 text-[#285c35] shrink-0" />
                        Fordele ved denne ydelse
                      </h3>
                      <div 
                        className="text-stone-700 text-base sm:text-lg [&_ul]:space-y-2 [&_li]:flex [&_li]:items-start [&_li]:gap-2.5 [&_li]:leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: service.benefits.da }}
                      />
                    </div>
                  )}
                </div>
              )}

              {allImages.length > 0 && (
                <div className="mt-16 pt-8 border-t border-stone-100">
                  <h3 className="text-2xl font-bold mb-8 text-stone-900">Galleri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {allImages.map((img) => (
                      <div 
                        key={img.id} 
                        className="group relative cursor-pointer overflow-hidden rounded-lg aspect-video shadow-md hover:shadow-lg transition-all"
                        onClick={() => setSelectedImage(img)}
                      >
                        <img 
                          src={imageUrl(img.url, 'width=800,quality=85,format=auto')} 
                          alt={`${img.title || service.title?.da || 'Skovservice'} udført af TH Skovservice på Mors og i Thy`} 
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                        />
                        <div className="absolute inset-0 bg-stone-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white">
                            <Maximize2 className="w-6 h-6" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-8">
              <div className="bg-emerald-900 text-white p-8 rounded-xl sticky top-32 shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Få et tilbud</h3>
                <p className="text-emerald-100/80 mb-8 leading-relaxed">
                  Kontakt os i dag for en uforpligtende snak om din opgave. Vi besigtiger gerne opgaven gratis.
                </p>
                <div className="space-y-4">
                  <a 
                    href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} 
                    className="flex items-center gap-4 p-4 bg-emerald-800/80 rounded-lg hover:bg-emerald-700/80 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-700 flex items-center justify-center shrink-0">
                      <Phone className="w-5 h-5 text-emerald-200" />
                    </div>
                    <div>
                      <p className="text-xs text-emerald-300 font-medium">Ring til os</p>
                      <p className="font-bold text-lg">{contactInfo.phone}</p>
                    </div>
                  </a>
                  <a 
                    href={`mailto:${contactInfo.email}`} 
                    className="flex items-center gap-4 p-4 bg-emerald-800/80 rounded-lg hover:bg-emerald-700/80 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-emerald-700 flex items-center justify-center shrink-0">
                      <Mail className="w-5 h-5 text-emerald-200" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-emerald-300 font-medium">Send en mail</p>
                      <p className="font-bold text-sm truncate">{contactInfo.email}</p>
                    </div>
                  </a>
                </div>
                <Link 
                  to="/kontakt" 
                  className="mt-8 w-full block text-center py-4 bg-white text-emerald-900 rounded-lg font-bold hover:bg-emerald-50 transition-colors shadow-sm"
                >
                  Gå til kontaktformular
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-stone-900/95 flex items-center justify-center p-4 md:p-12"
            onClick={() => setSelectedImage(null)}
          >
            <button 
              className="absolute top-8 right-8 text-white hover:text-emerald-400 transition-colors"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-10 h-10" />
            </button>
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="max-w-6xl w-full max-h-full overflow-hidden rounded-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={imageUrl(selectedImage.url, 'width=1600,quality=90,format=auto')} 
                alt={`${selectedImage.title || service.title?.da || 'Skovservice'} - TH Skovservice`} 
                className="w-full h-auto object-contain max-h-[85vh] rounded"
              />
              <div className="bg-stone-900/80 backdrop-blur-md p-6 text-white text-center mt-4 rounded-lg">
                <h3 className="text-2xl font-bold">{selectedImage.title}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
